import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Package, Globe, Copy, Stethoscope, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useProcedureBundles, ProcedureBundle } from '@/hooks/useProcedureBundles';

export function BundleManagement() {
  const { bundles, loading } = useProcedureBundles();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleCustomize = (bundleName: string) => {
    toast({ title: 'Customization Started', description: `"${bundleName}" cloned for sub-specialty customization.` });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mr-2" />
          <span className="text-sm text-muted-foreground">Loading bundles…</span>
        </CardContent>
      </Card>
    );
  }

  const getPrimaryCpt = (bundle: ProcedureBundle) =>
    bundle.items.find(i => i.is_primary)?.procedure.cpt_code ?? '—';

  const getSecondaryCpts = (bundle: ProcedureBundle) =>
    bundle.items.filter(i => !i.is_primary).map(i => i.procedure.cpt_code);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                Global Procedure Bundle Library
              </CardTitle>
              <CardDescription>
                {bundles.length} active bundles — standard templates that surgeons can customize
              </CardDescription>
            </div>
            <Button size="sm" className="gap-1.5">
              <Package className="w-3.5 h-3.5" />
              New Global Bundle
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {bundles.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">No active bundles found.</p>
          )}
          {bundles.map(bundle => {
            const primaryCpt = getPrimaryCpt(bundle);
            const secondaryCpts = getSecondaryCpts(bundle);

            return (
              <div key={bundle.id} className="border rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedId(expandedId === bundle.id ? null : bundle.id)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <Stethoscope className="w-4 h-4 text-primary shrink-0" />
                    <div>
                      <span className="font-semibold text-sm">{bundle.name}</span>
                      <div className="flex gap-1.5 mt-1 flex-wrap">
                        <Badge variant="outline" className="text-[10px] font-mono">{primaryCpt}</Badge>
                        {secondaryCpts.length > 0 && (
                          <Badge variant="secondary" className="text-[10px]">
                            +{secondaryCpts.length} codes
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-[10px] gap-0.5">
                          <Globe className="w-2.5 h-2.5" />
                          {bundle.specialty}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </button>

                {expandedId === bundle.id && (
                  <div className="px-4 pb-4 space-y-3 border-t bg-muted/10">
                    <div className="pt-3">
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">Included CPTs</p>
                      <div className="flex flex-wrap gap-1.5">
                        <Badge className="font-mono text-xs">{primaryCpt} (Primary)</Badge>
                        {secondaryCpts.map(c => (
                          <Badge key={c} variant="outline" className="font-mono text-xs">{c}</Badge>
                        ))}
                      </div>
                    </div>

                    {bundle.notes && (
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                        <p className="text-xs text-muted-foreground">{bundle.notes}</p>
                      </div>
                    )}

                    {bundle.documentation_checklist.length > 0 && (
                      <>
                        <Separator />
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1.5">Documentation Checklist</p>
                          <ul className="text-xs text-muted-foreground space-y-0.5 list-disc pl-4">
                            {bundle.documentation_checklist.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </>
                    )}

                    <div className="flex gap-2 pt-1">
                      <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={() => handleCustomize(bundle.name)}>
                        <Copy className="w-3 h-3" />
                        Sub-specialty Customize
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
